export const ROUTES = {
  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  ACTIVATE_ACCOUNT: '/activate-account/:uid/:token',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD_BASE: '/reset-password',
  RESET_PASSWORD: '/reset-password/:uid/:token',

  // Admin
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_OLYMPIADS: '/admin/olympiads',
  ADMIN_OLYMPIAD_NEW: '/admin/olympiads/new',
  ADMIN_OLYMPIAD_EDIT: '/admin/olympiads/:id/edit',
  ADMIN_OLYMPIAD_PARTICIPANTS: '/admin/olympiads/:id/participants',
  ADMIN_TASKS: '/admin/tasks',
  ADMIN_RESULTS: '/admin/results',
  ADMIN_RESULTS_OVERALL: '/admin/results/overall-rate',
  ADMIN_RESULTS_COUNTRIES: '/admin/results/countries-rate',
  ADMIN_CHAT: '/admin/chat',
  ADMIN_CHAT_USERS: '/admin/chat/users',
  ADMIN_SUPPORT: '/admin/support',
  ADMIN_PAYMENTS: '/admin/payments',
  ADMIN_CERTIFICATES: '/admin/certificates',
  ADMIN_PROFILE: '/admin/profile',
  ADMIN_SETTINGS: '/admin/settings',

  // Participant
  PARTICIPANT_DASHBOARD: '/participant/dashboard',
  PARTICIPANT_OLYMPIADS: '/participant/olympiads',
  PARTICIPANT_OLYMPIAD_DETAIL: '/participant/olympiads/:id',
  PARTICIPANT_TASKS: '/participant/tasks',
  PARTICIPANT_TASK_DETAIL: '/participant/tasks/:id',
  PARTICIPANT_RESULTS: '/participant/results',
  PARTICIPANT_MY_WAY: '/participant/my-way',
  PARTICIPANT_RATE_OLYMPIAD: '/participant/rate/olympiad',
  PARTICIPANT_RATE_OVERALL: '/participant/rate/overall',
  PARTICIPANT_PAYMENTS: '/participant/payments',
  PARTICIPANT_CERTIFICATES: '/participant/certificates',
  PARTICIPANT_INSTRUCTIONS: '/participant/instructions',
  PARTICIPANT_FAQ: '/participant/faq',
  PARTICIPANT_MESSAGES: '/participant/messages',
  PARTICIPANT_PERSONAL: '/participant/personal',
  PARTICIPANT_SETTINGS: '/participant/settings',

  // Representative
  REPRESENTATIVE: '/representative',
  REPRESENTATIVE_MEMBERS: '/representative/members',
  /** Карточка участника: `/representative/members/:id?tab=…`. Собирать через `memberCardPath`. */
  REPRESENTATIVE_MEMBER: '/representative/members/:id',
  REPRESENTATIVE_MEMBERS_PROGRESS: '/representative/members/progress',
  REPRESENTATIVE_MEMBERS_PAYMENTS: '/representative/members/payments',
  REPRESENTATIVE_MEMBERS_PERSONAL: '/representative/members/personal',
  REPRESENTATIVE_CHAT: '/representative/chat',
  REPRESENTATIVE_RESULTS: '/representative/results',
  REPRESENTATIVE_RESULTS_RATE: '/representative/results/rate',
  REPRESENTATIVE_PROFILE: '/representative/profile',
  REPRESENTATIVE_SETTINGS: '/representative/profile/settings',
} as const

/** Shared URL search-param names used to deep-link into filtered pages. */
export const QUERY_PARAMS = {
  olympiadId: 'olympiad_id',
  /** Какое поле профиля подсветить при переходе: `?focus=city`. Нужен там,
   *  где участника отправляют чинить конкретную строку профиля, а не «куда-то
   *  в настройки» — например когда в его городе нет площадки олимпиады. */
  focus: 'focus',
  /** Какой канал открыть в разделе «Сообщения»: `?channel=support`. Нужен
   *  редиректам с прежних адресов раздела — иначе ссылка на инбокс поддержки
   *  приводила бы в объявления. Значения — `AdminChannel`. */
  channel: 'channel',
  /** Из какой ленты открыта задача: `?source=daily|general`. Обязателен в
   *  каждой ссылке на деталь — у лент разные пространства id, и без него
   *  число в адресе ничего не значит. Значения — `ParticipantTaskSource`. */
  source: 'source',
  /** Вкладка карточки участника у представителя: `?tab=progress`. Значения — `MemberCardTab`. */
  tab: 'tab',
  /** `?id=` прежних адресов `/representative/members/{progress,payments,personal}`;
   *  нужен только их редиректам, новая карточка несёт id в пути. */
  legacyMemberId: 'id',
} as const

/** Вкладки карточки участника у представителя. */
export const MemberCardTab = {
  profile: 'profile',
  progress: 'progress',
  results: 'results',
  statuses: 'statuses',
} as const
export type MemberCardTab = (typeof MemberCardTab)[keyof typeof MemberCardTab]

/** Ссылка на карточку участника; без вкладки открывается «Профиль». */
export function memberCardPath(id: number | string, tab?: MemberCardTab): string {
  const base = `${ROUTES.REPRESENTATIVE_MEMBERS}/${id}`
  return tab && tab !== MemberCardTab.profile ? `${base}?${QUERY_PARAMS.tab}=${tab}` : base
}

/** Значения `?focus=` — поля профиля, на которые умеет наводить ссылка. */
export const ProfileFocusField = {
  city: 'city',
} as const
export type ProfileFocusField = (typeof ProfileFocusField)[keyof typeof ProfileFocusField]

/** Ссылка в профиль участника с наведением на поле: `/participant/personal?focus=…`. */
export function profileFocusPath(field: ProfileFocusField): string {
  return `${ROUTES.PARTICIPANT_PERSONAL}?${QUERY_PARAMS.focus}=${field}`
}
